<!-- src/components/Global/ParticleBackground.vue -->
<template>
  <canvas ref="cv" class="particle-canvas" :style="{ background: canvasBg }"></canvas>
</template>

<script>
import { useSettingsStore } from "@/stores/settings";

export default {
  name: "ParticleBackground",
  data() {
    return {
      ctx: null,
      w: 0,
      h: 0,
      particles: [],
      raf: null,
      mouse: { x: -9999, y: -9999 },
    };
  },
  computed: {
    // En tema oscuro las neuronas van claras (visibles sobre fondo oscuro);
    // en tema claro van negras.
    particleRGB() {
      const settings = useSettingsStore();
      return settings.theme === "dark" ? "255,255,255" : "255,255,255";
    },
    // Degradado del fondo: mas oscuro en tema dark, mas claro en light.
    canvasBg() {
      return useSettingsStore().theme === "dark"
        ? "linear-gradient(90deg, #005FAC 0%, #012B59 100%)"
        : "linear-gradient(90deg, #005FAC 0%, #E2E3E2 100%)";
    },
  },
  mounted() {
    const canvas = this.$refs.cv;
    this.ctx = canvas.getContext("2d");

    const resize = () => {
      this.w = canvas.width = window.innerWidth;
      this.h = canvas.height = window.innerHeight;
      this.initParticles();
    };

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("mouseleave", this.onMouseLeave);

    resize();
    this.loop();

    this._cleanup = () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", this.onMouseMove);
      window.removeEventListener("mouseleave", this.onMouseLeave);
      cancelAnimationFrame(this.raf);
    };
  },
  beforeUnmount() {
    this._cleanup && this._cleanup();
  },
  methods: {
    onMouseMove(e) {
      this.mouse.x = e.clientX;
      this.mouse.y = e.clientY;
    },
    onMouseLeave() {
      this.mouse.x = -9999;
      this.mouse.y = -9999;
    },
    initParticles() {
      //const count = Math.min(120, Math.floor((this.w * this.h) / 15000))
      // Mas densidad de neuronas: subimos el tope y bajamos el divisor.
      const count = Math.min(500, Math.floor((this.w * this.h) / 8000));
      this.particles = Array.from({ length: count }).map(() => ({
        x: Math.random() * this.w,
        y: Math.random() * this.h,
        vx: (Math.random() - 0.5) * 0.6,
        vy: (Math.random() - 0.5) * 0.6,
      }));
    },
    loop() {
      this.raf = requestAnimationFrame(this.loop);

      const ctx = this.ctx;
      if (!ctx) return;

      ctx.clearRect(0, 0, this.w, this.h);

      const rgb = this.particleRGB;
      const maxDist = 140;
      const maxDist2 = maxDist * maxDist;

      for (const p of this.particles) {
        // Movimiento base
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > this.w) p.vx *= -1;
        if (p.y < 0 || p.y > this.h) p.vy *= -1;

        const dx = p.x - this.mouse.x;
        const dy = p.y - this.mouse.y;
        const dist = dx * dx + dy * dy;

        if (dist < 200 * 200) {
          const distance = Math.sqrt(dist);
          const force = (200 - distance) / 200;
          const strength = 0.6;

          p.x += (dx / distance) * force * strength;
          p.y += (dy / distance) * force * strength;
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${rgb},0.8)`;
        ctx.fill();
      }

      for (let i = 0; i < this.particles.length; i++) {
        for (let j = i + 1; j < this.particles.length; j++) {
          const a = this.particles[i];
          const b = this.particles[j];

          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;

          if (d2 < maxDist2) {
            const alpha = 1 - d2 / maxDist2;

            ctx.strokeStyle = `rgba(${rgb},${alpha * 0.4})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
    },
  },
};
</script>

<style scoped>
.particle-canvas {
  position: fixed;
  inset: 0;
  z-index: 8;
  pointer-events: none;
}
</style>
